import {Product} from "../models/product";
import {Query, QueryState} from "../models/query";
import {config} from "../config";
import qs from 'qs';
import App from "../components/app";
import {Like} from "../models/customer";
import {Collection} from "../models/collection";
import {NY_CountryService} from "./country-service";

class ProductService {

  async loadProducts(q: Query): Promise<QueryState>{
    if (q && q.filters) {
      if (q.filters.country === 'en') {
        q.filters.country = 'de'
      }
      if (q.filters.gender === 'NONE') {
        q.filters.gender = []
      }
    }

    const queryString = qs.stringify(q);

    let url = `${config.backendBasePath}/query?${queryString}`;
    const response = await fetch(
        url, {
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'GET'
        });
    if (response.ok) {
      let result = await response.json();
      let products: Product[] = result.items.map(product => new Product(product));

      return new QueryState(result.totalCount, products, q.limit, q.offset);
    } else {
      console.debug("can not load products");
      return new QueryState(0, [], q.limit, q.offset);
    }
  }

  async loadMatchingProducts(productId: string, variantId: string, limit: number): Promise<Product[]> {
    let url = `${config.backendBasePath}/product/matchingProducts?country=${App.countryCode}&id=${productId}&variantId=${variantId}&limit=${limit}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'GET'
    });
    if (response.ok) {
      let result = await response.json();
      return result.map(product => new Product(product)).filter(product => product.variants.length > 0);
    }
  }

  async loadProductById (id: string, country: string): Promise<Product> {
    let countryCode;
    if (country === 'en') {
      countryCode = 'de'
    } else {
      countryCode = country
    }
    const url = `${config.backendBasePath}/product/${id}?country=${countryCode}`;
    const response = await fetch(
        url, {
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'GET'
        });

    if (response.ok) {
      let result = await response.json();
      return new Product(result);
    } else {
      console.debug("can not load product");
      return null;
    }
  }

  async loadProductsForLikes (likes: Like[], country: string): Promise<QueryState> {
    let countryCode;
    if (country === 'en') {
      countryCode = 'de'
    } else {
      countryCode = country
    }
    const ids = likes.map(like => like.productId);
    const url = `${config.backendBasePath}/products?country=${countryCode}&ids=${ids.join(',')}`;
    const response = await fetch(
        url, {
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'GET'
        });

    if (response.ok) {
      let result = await response.json();
      let products: Product[] = [];

      if (result) {
        likes.forEach(like => {
          result.forEach(it => {
            let product = new Product(it);
            let variant = product.getVariant(like.productId, like.variantId);
            if (variant) {
              product.variants = [variant];
              products.push(product);
            }
          });
        });
      }

      return new QueryState(products.length, products, products.length, 0);
    } else {
      console.debug("can not load products");
      return new QueryState(0, [], -1, -1);
    }
  }

  async loadCollections(collectionIds: string, countryCode: string): Promise<Collection[]> {
    if (!collectionIds) {
      return [];
    }
    const url = `${config.backendBasePath}/collection/products?country=${countryCode}&ids=${collectionIds}`;
    const response = await fetch(
      url, {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET'
      });

    if (response.ok) {
      const result = await response.json();
      return result.map(c => new Collection(c));
    } else {
      console.debug('failed to load collection');
      return [];
    }
  }

  // noinspection JSMethodCanBeStatic
  getTranslationKey(product: Product): string {
    if (product) {
      if (product.descriptions && product.descriptions.length > 0) {
        const language = NY_CountryService.getLanguageForCountry(App.countryCode);
        const description = product.descriptions.find(d => d.language.toUpperCase() === language.toUpperCase());
        const fallbackDescription = product.descriptions.find(d => d.language.toUpperCase() === 'EN');
        if (description || fallbackDescription) {
          return description?.description || fallbackDescription?.description;
        }
      }

      let filterKey = product.web_category.trim().toLowerCase();
      filterKey = filterKey.replace(new RegExp(' ', 'g'), '_');
      if (product.customer_group.toLowerCase() === 'male') {
        return `webcat_haka_singular_${filterKey}`
      } else {
        return `webcat_singular_${filterKey}`
      }
    }
    return '';
  }
}

export let productService = new ProductService();